import { createSelector } from 'reselect';
import { clone } from '../utils';

import Logger from '../Logger';

const meRolesSelect = (state) => state.me.roles;

const myIdSelector = (state) => state.me.id;

const roomPermissionsSelect = (state) => state.room.roomPermissions;
const roomAllowWhenRoleMissing = (state) => state.room.allowWhenRoleMissing;

const producersValueSelector = (state) => state.producers ? Object.values(state.producers) : [];
const consumersValueSelector = (state) => state.consumers ? Object.values(state.consumers) : [];

//const spotlightsSelector = (state) => state.room.spotlights;
const spotlightsSelector = (state) => state.room.layoutConfig.data.map(v => v.peerId).filter(v => v);

const layoutConfigSelector = (state) => state.room.layoutConfig;

const chairPeerIdSelector = (state) => state.room.chairPeerId;
const lockPeerIdSelector = (state) => state.room.lockPeerId;

const cohostSuidsSelector = (state) => state.room.cohostSuids;

const peersSelector = (state) => state.peers;
const lobbyPeersSelector = (state) => state.lobbyPeers;
const getPeerConsumerIds = (state, id) => (state.peers[id] ? state.peers[id].consumers : null);

const isHiddenSelect = (state) => state.room.hideSelfView;
const getAllConsumers = (state) => state.consumers;

const peersKeySelector = createSelector(
	peersSelector,
	(peers) => {

		return Object.keys(peers)
	}
);

const logger = new Logger('Selectors');

/**
 * state.me.id
 * state.room.chairPeerId
 */
export const meIsModeratorSelector = createSelector(
	myIdSelector,
	chairPeerIdSelector,
	(myId, chairPeerId) => {

		return myId === chairPeerId
	}
);

/**
 * state.me.id
 * state.peers
 * state.room.chairPeerId
 */
export const meIsCohostSelector = createSelector(
	myIdSelector,
	peersSelector,
	chairPeerIdSelector,
	(myId, peers, chairPeerId) => {

		const _peer = peers[myId];
		if (_peer && _peer.isCohost && myId !== chairPeerId) {

			return true;
		} else {

			return false;
		}
	}
);

export const peersValueSelector = createSelector(
	peersSelector,
	(peers) => {

		return Object.values(peers)
	}
);

export const lobbyPeersKeySelector = createSelector(
	lobbyPeersSelector,
	(lobbyPeers) => Object.keys(lobbyPeers)
);

export const micProducersSelector = createSelector(
	producersValueSelector,
	(producers) => {

		return producers.filter((producer) => producer.source === 'mic')
	}
);

export const webcamProducersSelector = createSelector(
	producersValueSelector,
	(producers) => {

		return producers.filter((producer) => producer.source === 'webcam')
	}
);

export const screenProducersSelector = createSelector(
	producersValueSelector,
	(producers) => {

		return producers.filter((producer) => producer.source === 'screen')
	}
);

export const extraVideoProducersSelector = createSelector(
	producersValueSelector,
	(producers) => {

		return producers.filter((producer) => producer.source === 'extravideo')
	}
);

export const micProducerSelector = createSelector(
	producersValueSelector,
	(producers) => {

		return producers.find((producer) => producer.source === 'mic')
	}
);

export const webcamProducerSelector = createSelector(
	producersValueSelector,
	(producers) => {

		return producers.find((producer) => producer.source === 'webcam')
	}
);

export const screenProducerSelector = createSelector(
	producersValueSelector,
	(producers) => {

		return producers.find((producer) => producer.source === 'screen')
	}
);

export const screenConsumerSelector = createSelector(
	consumersValueSelector,
	(consumers) => {

		if (!consumers) {

			return null;
		}

		const _screenConsumer = consumers.find((consumer) => consumer.source === 'screen');

		return _screenConsumer;
	}
);

export const spotlightScreenConsumerSelector = createSelector(
	spotlightsSelector,
	consumersValueSelector,
	(spotlights, consumers) => {

		return consumers.filter(
			(consumer) => consumer.source === 'screen' && spotlights.includes(consumer.peerId)
		)
	}
);

export const spotlightExtraVideoConsumerSelector = createSelector(
	spotlightsSelector,
	consumersValueSelector,
	(spotlights, consumers) => {

		return consumers.filter(
			(consumer) => consumer.source === 'extravideo' && spotlights.includes(consumer.peerId)
		)
	}
);

export const passiveMicConsumerSelector = createSelector(
	spotlightsSelector,
	consumersValueSelector,
	(spotlights, consumers) => {

		return consumers.find(
			(consumer) => consumer.source === 'mic' && !spotlights.includes(consumer.peerId)
		)
	}
);

export const spotlightsLengthSelector = createSelector(
	spotlightsSelector,
	(spotlights) => spotlights.length
);

export const spotlightPeersSelector = createSelector(
	spotlightsSelector,
	peersKeySelector,
	(spotlights, peers) => peers.filter((peerId) => spotlights.includes(peerId))
);

export const layoutConfigPeersSelector = createSelector(
	layoutConfigSelector,
	peersSelector,
	(layoutConfig, peers) => {

	    const _layoutConfig = clone(layoutConfig);
		const _viewsArea = [];

		for (const _viewArea of _layoutConfig.data) {

			const _peer = peers[_viewArea.peerId];
			_viewArea.videoMuted = true;

			if (_peer) {

				_viewArea.videoMuted = _peer.videoMuted;
				_viewArea.displayName = _peer.displayName;
			} else {

				_viewArea.displayName = null;
				_viewArea.videoId = null;
				_viewArea.peerId = null;
			}

			if (_viewArea.source === 'share') {

				_viewArea.videoMuted = false;
			}

			_viewsArea.push(_viewArea)
		}

		_layoutConfig.data = _viewsArea;

		//logger.debug('触发 layoutConfigPeersSelector 后 生成界面 layoutConfig:', JSON.stringify(_layoutConfig), JSON.stringify(peers));

		return _layoutConfig;
	}
)

/**
 * 参会人中的主持人
 */
const chairPeerSelector = createSelector(
	chairPeerIdSelector,
	peersValueSelector,
	(chairPeerId, peers) => {

		return peers.filter(
			(peer) => peer.id === chairPeerId
		)
	}
);

/**
 * 参会人中联席主持人(排除主持人,入会先后顺序排序)
 */
const cohostPeersSortedSelector = createSelector(
	chairPeerIdSelector,
	cohostSuidsSelector,
	peersValueSelector,
	(chairPeerId, cohostSuids, peers) => {
		
		const _cohostPeers = peers.filter(
			(peer) => peer.id !== chairPeerId && peer.isCohost
		);

		const _sortCohostPeers = [];

		for (const suid of cohostSuids) {

			const _cohostPeer = _cohostPeers.find((v) => v.suid == suid);

			if (_cohostPeer) {

				_sortCohostPeers.push(_cohostPeer);
			}
		}

		logger.debug(`cohostPeersSortedSelector -> ${JSON.stringify(cohostSuids)}  ${JSON.stringify(_sortCohostPeers)}`);

		return _sortCohostPeers;
	}
)

/**
 * 参会人中焦点(排除主持人、联席主持人列表)
 */
const lockPeerSelector = createSelector(
	lockPeerIdSelector,
	chairPeerIdSelector,
	cohostPeersSortedSelector,
	peersValueSelector,
	(lockPeerId, chairPeerId, cohostPeers, peers) => {
		return peers.filter((peer) => {

			return peer.id === lockPeerId && peer.id !== chairPeerId && !cohostPeers.map((v) => v.id).includes(peer.id)
		})
	}
)

/**
 * 其它参会人排序(排除主持人、联席主持人、焦点后按时间排序)
 */
 const otherPeersSortedSelector = createSelector(
	lockPeerIdSelector,
	chairPeerIdSelector,
	cohostPeersSortedSelector,
	peersValueSelector,
	(lockPeerId, chairPeerId, cohostPeers, peers) => {

		return peers.filter((peer) =>  {

			return ![lockPeerId, chairPeerId].includes(peer.id) && !cohostPeers.map((v) => v.id).includes(peer.id)
		}).sort((a, b) => {
			if (a.id.split('-').length == 2 && b.id.split('-').length) {

				return parseInt(a.id.split('-')[1]) - parseInt(b.id.split('-')[1]);
			} else {

				return a.updateAt - b.updateAt
			}			
		})
	}
)

/**
 * 参会人组合列表
 */
export const participantListSelector = createSelector(
	chairPeerSelector,
	cohostPeersSortedSelector,
	lockPeerSelector,
	otherPeersSortedSelector,
	(chairPeer, cohostPeers, lockPeer, otherPeers) => {

		return [
			...chairPeer,
			...cohostPeers,
			...lockPeer,
			...otherPeers
		]
	}
);

/**
 * 举手参会人按时间先后顺序
 */
const raisedHandSortedPeers = createSelector(
	peersValueSelector,  // 所有参会人
	(peers) => peers.filter((peer) => peer.raisedHand)
		.sort((a, b) => a.raisedHandTimestamp - b.raisedHandTimestamp)
);

/**
 * 在当前视频窗口中的参会人排序
 */
export const spotlightSortedPeersSelector = createSelector(
	spotlightsSelector,
	peersValueSelector,
	(spotlights, peers) =>
		peers.filter((peer) => spotlights.includes(peer.id) && !peer.raisedHand)
			//.sort((a, b) => String(a.displayName || '').localeCompare(String(b.displayName || '')))
);

/**
 * 其它参会人排序
 */
const peersSortedSelector = createSelector(
	spotlightsSelector,
	peersValueSelector,
	(spotlights, peers) => peers.filter(

		(peer) => !spotlights.includes(peer.id) && !peer.raisedHand

	).sort(

		(a, b) => String(a.displayName || '').localeCompare(String(b.displayName || ''))

	)
);

/**
 * 布局设置里的用户列表
 */
export const spotlightViewSelector = createSelector(
	participantListSelector,
	screenConsumerSelector,
	(participants, screenConsumer) => {

		let _participants = [
			{
				id           : 'speech',
				picture      :'/svg/meetingLayout/speech.svg',
				displayName  : 'Speech'
			},
			...participants
		];

		if (screenConsumer) {

			_participants = [{
					id: 'share_' + screenConsumer.peerId,
					picture: '/svg/meetingLayout/share.svg',
					displayName: 'shareContent'
				},
				..._participants
			];
		}

		return _participants;
	}
);

export const peersLengthSelector = createSelector(
	peersSelector,
	(peers) => Object.values(peers).length
);

export const passivePeersSelector = createSelector(
	peersValueSelector,
	spotlightsSelector,
	(peers, spotlights) => peers.filter
		(

			(peer) => !spotlights.includes(peer.id)

		).sort(

			(a, b) => String(a.displayName || '').localeCompare(String(b.displayName || ''))

		)
);

export const raisedHandsSelector = createSelector(
	peersValueSelector,
	(peers) => peers.reduce(
		(a, b) => (a + (b.raisedHand ? 1 : 0)), 0
	)
);

export const videoBoxesSelector = createSelector(
	isHiddenSelect,
	spotlightsLengthSelector,
	screenProducersSelector,
	spotlightScreenConsumerSelector,
	extraVideoProducersSelector,
	spotlightExtraVideoConsumerSelector,
	(
		isHidden,
		spotlightsLength,
		screenProducers,
		screenConsumers,
		extraVideoProducers,
		extraVideoConsumers
	) =>
	{
		return (
			spotlightsLength +
			(isHidden ? 0 : 1) +
			(isHidden ? 0 : screenProducers.length) +
			screenConsumers.length +
			(isHidden ? 0 : extraVideoProducers.length) +
			extraVideoConsumers.length
		)
	}
);

export const meVideoBoxesSelector = createSelector(
	isHiddenSelect,
	screenProducersSelector,
	extraVideoProducersSelector,
	(
		isHidden,
		screenProducers,
		extraVideoProducers
	) => {

		return (
			(isHidden ? 0 : 1) +
			(isHidden ? 0 : screenProducers.length) +
			(isHidden ? 0 : extraVideoProducers.length)
		)
	}
);

export const meProducersSelector = createSelector(
	micProducerSelector,
	webcamProducerSelector,
	screenProducerSelector,
	extraVideoProducersSelector,
	(micProducer, webcamProducer, screenProducer, extraVideoProducers) =>
	{
		return {
			micProducer,
			webcamProducer,
			screenProducer,
			extraVideoProducers
		};
	}
);

export const makePeerConsumerSelector = () =>
{
	return createSelector(
		getPeerConsumerIds,
		getAllConsumers,
		(consumerIds, allConsumers) =>
		{
			if (!consumerIds) {

				return null;
			}

			const consumersArray = consumerIds.map((consumerId) => allConsumers[consumerId]).filter(consumer => consumer);

			const micConsumer = consumersArray.find((consumer) => consumer.source === 'mic');
			const webcamConsumer = consumersArray.find((consumer) => consumer.source === 'webcam');
			const screenConsumer = consumersArray.find((consumer) => consumer.source === 'screen');
			const extraVideoConsumers = consumersArray.filter((consumer) => consumer.source === 'extravideo');

			return {

				micConsumer,
				webcamConsumer,
				screenConsumer,
				extraVideoConsumers
			};
		}
	);
};

// Very important that the Components that use this
// selector need to check at least these state changes:
//
// areStatesEqual : (next, prev) =>
// {
// 		return (
// 			prev.room.roomPermissions === next.room.roomPermissions &&
// 			prev.room.allowWhenRoleMissing === next.room.allowWhenRoleMissing &&
// 			prev.peers === next.peers &&
// 			prev.me.roles === next.me.roles
// 		);
// }
export const makePermissionSelector = (permission) =>
{
	return createSelector(
		meRolesSelect,
		roomPermissionsSelect,
		roomAllowWhenRoleMissing,
		peersValueSelector,
		(roles, roomPermissions, allowWhenRoleMissing, peers) =>
		{
			if (!roomPermissions) {

				return false;
			}

			const permitted = roles && roles.some((role) => {

				return roomPermissions[permission].includes(role)
			});

			if (permitted) {

				return true;
			}

			if (!allowWhenRoleMissing) {

				return false;
			}

			// Allow if config is set, and no one is present
			if (allowWhenRoleMissing.includes(permission) &&
				peers.filter(
					(peer) => {

						return peer && peer.roles && peer.roles.some(
							(role) => roomPermissions[permission].includes(role)
						)
					}).length === 0
			) {

				return true;
			}

			return false;
		}
	);
};